import React, { useState } from 'react';
import { Button, Form, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import { Lock, Key } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import useForm from '../../hooks/useForm';

const FirstLogin = () => {
    const { user, changePasswordFirstLogin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const { values, handleChange, errors, validateForm } = useForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const validationSchema = {
        oldPassword: {
            required: true,
            errorMessage: 'Current password is required'
        },
        newPassword: {
            required: true,
            min: 8,
            errorMessage: 'New password must be at least 8 characters'
        },
        confirmPassword: {
            required: true,
            errorMessage: 'Please confirm your new password'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');

        // Add custom validation for password match
        if (values.newPassword !== values.confirmPassword) {
            setError("New passwords don't match.");
            setLoading(false);
            return;
        }

        // Validate form
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const result = await changePasswordFirstLogin(values.oldPassword, values.newPassword);

            if (result.success) {
                setSuccessMessage('Password changed successfully! Redirecting to dashboard...');
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
            <Card className="shadow-sm" style={{ width: '100%', maxWidth: '500px' }}>
                <Card.Body className="p-4">
                    <div className="text-center mb-4">
                        <h2 className="mb-3">Changer votre mot de passe</h2>
                        <p className="text-muted">
                            Bienvenue {user?.fullName || 'à Sen OSCAR'}! Pour des raisons de sécurité, vous devez changer votre mot de passe avant de continuer.
                        </p>
                    </div>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {successMessage && <Alert variant="success">{successMessage}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Mot de passe actuel</Form.Label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Key size={18} />
                                </span>
                                <Form.Control
                                    type="password"
                                    name="oldPassword"
                                    value={values.oldPassword}
                                    onChange={handleChange}
                                    placeholder="Entrer votre mot de passe actuel"
                                    required
                                />
                            </div>
                            {errors.oldPassword && <Form.Text className="text-danger">{errors.oldPassword}</Form.Text>}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Nouveau Mot de passe</Form.Label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Lock size={18} />
                                </span>
                                <Form.Control
                                    type="password"
                                    name="newPassword"
                                    value={values.newPassword}
                                    onChange={handleChange}
                                    placeholder="Entrer votre nouveau mot de passe"
                                    required
                                />
                            </div>
                            {errors.newPassword && <Form.Text className="text-danger">{errors.newPassword}</Form.Text>}
                            <Form.Text className="text-muted">
                             Le mot de passe doit contenir au moins 8 caractères, incluant des chiffres et des caractères spéciaux.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Lock size={18} />
                                </span>
                                <Form.Control
                                    type="password"
                                    name="confirmPassword"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirmer votre mot de passe"
                                    required
                                />
                            </div>
                            {errors.confirmPassword && <Form.Text className="text-danger">{errors.confirmPassword}</Form.Text>}
                        </Form.Group>

                        <Button
                            variant="primary"
                            type="submit"
                            className="w-100"
                            disabled={loading}
                        >
                            {loading ? 'Modification mot de passe...' : 'Modifier mot de passe'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default FirstLogin;